import { useCallback } from 'react';
import { useLayoutStore } from './layoutStoreBase';
import type { MainContentTab } from './layoutStoreTypes';
import { TAB_SECTION_SCOPES, type TabSectionScope } from './tabSectionScopes';

export { TAB_SECTION_SCOPES, type TabSectionScope } from './tabSectionScopes';

const TAB_TYPE_TO_SECTION_SCOPE: Partial<
  Record<MainContentTab, TabSectionScope>
> = {
  settings: TAB_SECTION_SCOPES.applicationSettings,
  'proxy-settings': TAB_SECTION_SCOPES.proxySettings,
};

export function getTabSectionScopeForTabType(
  tabType: MainContentTab,
): TabSectionScope | undefined {
  return TAB_TYPE_TO_SECTION_SCOPE[tabType];
}

export function migrateLegacyTabSectionIfNeeded<TSection extends string>({
  scope,
  tabId,
  legacySection,
  defaultSection,
  clearLegacySection,
}: {
  scope: TabSectionScope;
  tabId: string;
  legacySection: TSection | undefined;
  defaultSection: TSection;
  clearLegacySection: () => void;
}): void {
  if (legacySection === undefined || legacySection === defaultSection) {
    return;
  }
  const stored = useLayoutStore.getState().tabSections[scope]?.[tabId];
  if (stored === undefined) {
    useLayoutStore
      .getState()
      .setTabSection({ scope, tabId, sectionId: legacySection });
  }
  clearLegacySection();
}

export function useTabSectionState<TSection extends string>({
  scope,
  tabId,
  defaultSection,
}: {
  scope: TabSectionScope;
  tabId: string;
  defaultSection: TSection;
}): {
  activeSection: TSection;
  setActiveSection: (section: TSection) => void;
} {
  const activeSection = useLayoutStore((state) => {
    const stored = state.tabSections[scope]?.[tabId];
    return stored === undefined ? defaultSection : (stored as TSection);
  });

  const setActiveSection = useCallback(
    (section: TSection) => {
      useLayoutStore
        .getState()
        .setTabSection({ scope, tabId, sectionId: section });
    },
    [scope, tabId],
  );

  return { activeSection, setActiveSection };
}
